import * as React from 'react';
import { createFileRoute, Outlet, useParams } from '@tanstack/react-router';
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { LoadingScreen } from '@/web/components/molecules/LoadingScreen';
import { categoriesQueryOptions, productsQueryOptions } from '@/web/lib/queryOptions';
import { useSuspenseQuery } from '@tanstack/react-query';
import { ProductsList } from '@/web/components/molecules/ProductsList';
import { ProductFormModal } from '@/web/components/molecules/ProductFormModal';
import { deleteProduct, updateProduct } from "@/web/lib/services";
import type { Product } from '@/types';
import { SubCategories } from '@/web/components/molecules/SubCategories';

export type ProductBrowseParameters = {
  pageNo?: number,
  subcategory?: string,
}

export const Route = createFileRoute('/products/$categoryId')({
  loaderDeps: ({ search: { pageNo, subcategory } }) => ({ pageNo, subcategory }),
  loader: async ({ params, context, deps: { pageNo, subcategory } }) => {
    const pageNoWithDefault = pageNo ?? 1;
    return context.queryClient?.ensureQueryData(productsQueryOptions(params.categoryId, subcategory, pageNoWithDefault));
  },
  validateSearch: (search: Record<string, unknown>): ProductBrowseParameters => {
    let queryParams: ProductBrowseParameters = {};
    if (search.pageNo) {
      queryParams.pageNo = Number(search?.pageNo);
    }
    if (search.subcategory) {
      queryParams.subcategory = String(search?.subcategory);
    }
    return queryParams;
  },
  preload: true,
  component: RouteComponent,
  pendingComponent: LoadingScreen,
});

function RouteComponent() {
  const params = useParams({ from: '/products/$categoryId' });
  const queryParams = Route.useSearch();
  const pageNo = queryParams.pageNo || 1;
  const subcategoryId = params.subcategoryId || null;
  const mode = 'edit';
  const [isOpen, setIsOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [product, setProduct] = React.useState<Product | null>(null);
  const queryClient = useQueryClient();

  const updateProductMutation = useMutation({
    mutationFn: updateProduct,
    onSuccess: () => {
      queryClient.invalidateQueries(productsQueryOptions(params.categoryId, subcategoryId, pageNo))
      setIsOpen(false);
    }
  });

  const deleteProductMutation = useMutation({
    mutationFn: deleteProduct,
    onSuccess: () => {
      queryClient.invalidateQueries(productsQueryOptions(params.categoryId, subcategoryId, pageNo))
      setIsOpen(false);
    }
  });

  const handleDelete = (productId: string) => {
    deleteProductMutation.mutate(productId);
  }

  const handleSubmit = (product: Product) => {
    updateProductMutation.mutate(product);
  }

  const editProduct = (product: Product) => () => {
    setProduct(product);
    setIsOpen(true);
  }

  const { data } = useSuspenseQuery(categoriesQueryOptions(params.categoryId));
  if (data) {
    const products = [];
    return (<div>
      <ProductFormModal
        isOpen={isOpen}
        isLoading={isLoading}
        mode={mode}
        onDelete={handleDelete}
        onSubmit={handleSubmit}
        onOpenChange={setIsOpen}
        product={product}
      />
      <div className="mb-4">
        <SubCategories categoryId={params.categoryId} subcategoryId={subcategoryId} pageNo={pageNo} categories={data.children} />
        <Outlet />
      </div>
    </div>)
  }
  return null;
}
